import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import * as microsoftTeams from '@microsoft/teams-js';
import { useHistory } from 'react-router-dom';
import SocialIntegrationResource from 'flow/api/socialintegration';

import ExternalSigninButton from 'flow/components/buttons/ExternalSigninButton';
import useAppContext from 'flow/hooks/useAppContext';
import { getBrand, getBrandKey } from 'flow/brands/brands';
import { isTeamsFullVersion } from 'flow/utils/app-utils';
import { electronBrandKeys, isTelavoxBrand } from 'flow/utils/brand-utils';
import { MSGOO_APP_REG_DEFAULT_VALUE } from 'flow/utils/social-integration-utils';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { setAppEmbedded } from 'flow/actions/embedded-app-actions';
import { showSignin } from 'flow/actions/navigate-actions';
import EmbeddedType from 'flow/types/embedded-type';
import { addGeneralLogEntry } from 'flow/utils/browser-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import {
    TEAMS_AUTH_FLOW_COMPLETE,
    TEAMS_AUTH_FLOW_START,
} from 'flow/constants/teams-auth-constants';

const authFlowStart = TEAMS_AUTH_FLOW_START;
const authFlowComplete = TEAMS_AUTH_FLOW_COMPLETE;

// Silent SSO that has not answered by then counts as failed, so the user is
// never left on "Logging in..." if teams-js or the verify call hangs.
export const SILENT_SSO_TIMEOUT_MS = 15000;

const startAuthFlowUrl = `${window.location.origin}${authFlowStart}`;
const callbackUrl = `${window.location.origin}${authFlowComplete}`;

export const isTeamsPopupUrl = (pathname: string): boolean =>
    pathname === authFlowStart || pathname === authFlowComplete;

const getLink = (url: string, label: string) => (
    <a
        target="_blank"
        rel="noopener noreferrer"
        href={url}
        className="text-link hover:underline"
    >
        {label}
    </a>
);

// This component triggers the popup-components
export const TeamsAuthenticationForm = () => {
    const history = useHistory();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { i18next, embedded } = useAppContext();
    const dispatch = useAppDispatch();
    const ssoTimeout = useRef<ReturnType<typeof setTimeout>>();
    // Set once silent SSO has fallen back, so a late answer from teams-js or
    // the verify call can't navigate away from the sign-in form.
    const ssoSettled = useRef(false);

    const isTeamsFv = isTeamsFullVersion(embedded);

    const setAuthError = () => setErrorMessage('FinishError');
    const setClientAuthError = () => setErrorMessage('FinishClientError');

    const handleNavigate = useCallback(
        (referer: string | null) => {
            clearTimeout(ssoTimeout.current);
            history.push(referer ?? '/settings');
        },
        [history],
    );

    const AuthenticationCallback = useMemo(
        () => ({
            onResult: () => {
                // Parse for a __ref query attribute that matches one of the tab paths
                // and push the browser history to there, if it exists and is parsable.
                // I.e., the user will be redirected back to where they were before they
                // got logged out.

                // Substring-call skips leading '?', if it exists at all
                const queryparams = window.location.search
                    .substring(1)
                    .split('&');
                const refererpre = '__ref=';
                const refererparam = queryparams.filter((param) =>
                    param.startsWith(refererpre),
                )[0];
                const referer = refererparam
                    ? refererparam.substring(refererpre.length)
                    : null;

                handleNavigate(referer);
            },
            onError: setAuthError,
            onClientError: setClientAuthError, // AccountNotBound
        }),
        [handleNavigate],
    );

    const authUsingSignInButton = () => {
        microsoftTeams.authentication
            .authenticate({
                url: startAuthFlowUrl,
                width: 800,
                height: 600,
            })
            .then((token) => {
                console.debug('Authentication successful');
                SocialIntegrationResource.finishExternalSignIn(
                    AuthenticationCallback,
                    token,
                    'microsoft',
                );
            })
            .catch((msg) => {
                console.debug('Authentication failed', msg);
                setAuthError();
            });
    };

    useEffect(() => {
        if (isTeamsFv) {
            const unlessSettled = (handler: () => void) => () => {
                if (!ssoSettled.current) {
                    handler();
                }
            };
            ssoTimeout.current = setTimeout(() => {
                ssoSettled.current = true;
                setErrorMessage('Timeout');
            }, SILENT_SSO_TIMEOUT_MS);
            microsoftTeams.authentication
                .getAuthToken()
                .then((token) => {
                    if (ssoSettled.current) {
                        return;
                    }
                    console.debug(
                        'Getting Microsoft Entra ID token successful',
                    );
                    SocialIntegrationResource.verifyTeamsAuthToken(
                        {
                            onResult: unlessSettled(
                                AuthenticationCallback.onResult,
                            ),
                            onError: unlessSettled(
                                AuthenticationCallback.onError,
                            ),
                            onClientError: unlessSettled(
                                AuthenticationCallback.onClientError,
                            ),
                        },
                        token,
                    );
                })
                .catch((error) => {
                    if (ssoSettled.current) {
                        return;
                    }
                    console.error('Error getting token', error);
                    addGeneralLogEntry('Error getting Teams token', `${error}`);
                    setAuthError();
                });
        }
        return () => clearTimeout(ssoTimeout.current);
    }, [AuthenticationCallback, isTeamsFv]);

    // When silent SSO fails for any reason, TEAMSFV continues as TEAMSFVMANUAL:
    // our own sign-in form, and from then on the same behaviour (a later 401
    // goes back to the sign-in form, not to SSO).
    useEffect(() => {
        if (isTeamsFv && errorMessage) {
            ssoSettled.current = true;
            clearTimeout(ssoTimeout.current);
            addGeneralLogEntry(
                'Teams silent SSO failed, falling back to manual sign-in:',
                errorMessage,
            );
            dispatch(setAppEmbedded(EmbeddedType.TEAMSFVMANUAL));
            dispatch(showSignin());
        }
    }, [dispatch, errorMessage, isTeamsFv]);

    const getBrandedSupportLink = () =>
        isTelavoxBrand(getBrandKey())
            ? getLink(
                  'https://telavox.com/integration/microsoft-365/',
                  'telavox.com',
              )
            : getLink(
                  'https://www.joinflow.com/en/integration/microsoft-teams/',
                  'joinflow.com',
              );

    const getBrandedHomeLink = () =>
        isTelavoxBrand(getBrandKey())
            ? getLink('https://telavox.com/', 'telavox.com')
            : getLink('https://www.joinflow.com/', 'joinflow.com');

    const renderMessage = () => {
        const brandName = getBrand().brandNameUIFriendly;

        switch (isTeamsFv ? null : errorMessage) {
            case 'FinishClientError':
                return (
                    <div className="box-border flex w-full flex-col items-center p-4">
                        <span>
                            {'⚠️ '}
                            {i18next.t('msteams.error.finishclienterror', {
                                brand: brandName,
                            })}{' '}
                            {getBrandedSupportLink()}
                        </span>
                    </div>
                );
            case 'FinishError':
                return (
                    <div className="box-border flex w-full flex-col items-center p-4">
                        <span>{i18next.t('msteams.error.finisherror')}</span>
                    </div>
                );
            default:
                return (
                    <div className="box-border flex w-full flex-col items-center p-4">
                        <span>
                            {isTeamsFv && i18next.t('msteams.loggingin')}
                            {!isTeamsFv && (
                                <>
                                    {i18next.t(
                                        'msteams.information.telavox_account',
                                        { brand: brandName },
                                    )}{' '}
                                    {getBrandedHomeLink()}
                                    {'. '}
                                    {i18next.t(
                                        'msteams.information.linked_account',
                                    )}
                                </>
                            )}
                        </span>
                    </div>
                );
        }
    };

    return (
        <div className="flex min-h-screen grow flex-col items-center justify-center bg-white">
            <div className="max-w-[500px] p-3">
                {!isTeamsFv && (
                    <ExternalSigninButton
                        type="microsoft"
                        clickHandler={authUsingSignInButton}
                    />
                )}
                {renderMessage()}
            </div>
        </div>
    );
};

// -----
// POP-UP PART OF THE AUTHENTICATION FLOW

// This component is used to complete the authentication flow using
// notifySuccess and notifyFailure. It will be rendered in a pop-up window
// created by the start of the authentication flow (and SSOAuthentication below),
// signalling back to the opener when flow is completed.
const TeamsAuthenticationPopupComplete = () => {
    useEffect(() => {
        microsoftTeams.app.initialize().then(() => {
            const group = /\?token=([^&]*)&source=microsoft/.exec(
                window.location.search,
            );
            if (group) {
                microsoftTeams.authentication.notifySuccess(group[1]);
            } else {
                microsoftTeams.authentication.notifyFailure('NoToken');
            }
        });
    }, []);

    return <div>{'Logging in...'}</div>;
};

const TeamsAuthenticationPopupStart = () => {
    useEffect(() => {
        microsoftTeams.app.initialize().then(() => {
            SocialIntegrationResource.startExternalSignIn(
                {
                    onResult: (result: string) => {
                        window.location.assign(result);
                    },
                    onError: (request: unknown) => {
                        console.error('Error', request);
                        microsoftTeams.authentication.notifyFailure(
                            'StartError',
                        );
                    },
                    onClientError: (request: unknown) => {
                        console.error('Client error', request);
                        microsoftTeams.authentication.notifyFailure(
                            'StartClientError',
                        );
                    },
                },
                'microsoft',
                MSGOO_APP_REG_DEFAULT_VALUE, // The Microsoft app registration is found based on branding, but appRegId needs to be included in the call
                // Flow UC uses the Flow brand; the UAE runs e&'s own Microsoft app.
                IS_UAE_HOST
                    ? electronBrandKeys.etisalat
                    : electronBrandKeys.flow,
                null,
                callbackUrl,
            );
        });
    }, []);

    return <div>{'Signing in...'}</div>;
};

export const TeamsAuthenticationPopup = () => {
    const { pathname } = window.location;

    let content;
    if (pathname === authFlowStart) {
        content = <TeamsAuthenticationPopupStart />;
    } else if (pathname === authFlowComplete) {
        content = <TeamsAuthenticationPopupComplete />;
    } else {
        content = <div>{'InvalidPath'}</div>;
    }

    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-white">
            {content}
        </div>
    );
};
